import { NoxusAlert } from "@/components/UI/NoxusAlert";
import { Control, Controller } from "react-hook-form";
import TiptapEditor, { ITiptapEditorConfig } from "./TiptapEditor/TiptapEditor";
import { Button } from "@/components/shadcn/button";
import { NoxusFormLabel } from "./NoxusFormLabel";
import { useRef } from "react";
import {
  type AdvancedChipOption,
  hasChipValues,
} from "./TiptapEditor/chipOptionsConfig";
import { SpotSelectField } from "./SelectField";
import { isTextPreviewContainer } from "@/utils/textPreview";
import { TextPreviewInput } from "./TextPreviewInput";
import { Editor } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "tiptap-markdown";
import ErrorBoundary from "@/components/ErrorBoundary";
import { plainTextToDoc } from "./TiptapEditor/plainText";

export interface RichTextValue {
  text: string;
  definition: Record<string, any>;
  isRaw?: boolean;
}

function isRichTextValue(obj: unknown): obj is RichTextValue {
  return (
    typeof obj === "object" &&
    obj !== null &&
    "text" in obj &&
    "definition" in obj
  );
}

/**
 * Recursively strips TipTap `definition` from any RichTextValue nested in a
 * config object before sending to the backend. The backend should only ever
 * see `{ text, isRaw }` — never the internal TipTap JSON doc.
 */
export function serializeConfigForBackend<T>(obj: T): T {
  if (!obj || typeof obj !== "object") return obj;
  if (Array.isArray(obj))
    return obj.map(serializeConfigForBackend) as unknown as T;
  if (isRichTextValue(obj)) {
    return obj.text as unknown as T;
  }
  return Object.fromEntries(
    Object.entries(obj).map(([k, v]) => [k, serializeConfigForBackend(v)])
  ) as T;
}

// Round-trip-safe parse via the same extensions TiptapEditor uses, so the
// resulting definition serialises back through getMarkdown() identically.
// A naive split-by-newline produced literal-text paragraphs that re-escaped
// every markdown special character on each save cycle.
const parseMarkdownToDoc = (markdown: string): Record<string, any> => {
  const editor = new Editor({
    extensions: [StarterKit.configure({ history: false }), Markdown],
    content: markdown,
  });
  const json = editor.getJSON();
  editor.destroy();
  return json;
};

export const getTemplateStringValue = (
  value: string | any,
  isSimpleText = false
) => {
  const text = typeof value === "string" ? value : value?.text || "";
  if (!text || text.length === 0) {
    return { text: "", definition: {}, isRaw: false };
  }
  return {
    text,
    definition: isSimpleText ? plainTextToDoc(text) : parseMarkdownToDoc(text),
    isRaw: false,
  };
};

interface TemplateOption {
  label: string;
  value: string;
}

interface IRichTextField {
  options?: AdvancedChipOption[];
  control: Control<any>;
  defaultValue?: RichTextValue;
  name: string;
  label?: string;
  tooltip?: string;
  placeholder?: string;
  unselectAllNodes?: () => any;
  isControlled?: boolean;
  config?: ITiptapEditorConfig;
  helperText?: string;
  templateOptions?: TemplateOption[];
  onKeyDown?: (e: React.KeyboardEvent) => void;
  required?: boolean;
  isDisabled?: boolean;
  isSimpleText?: boolean;
}

export const RichTextField = (props: IRichTextField) => {
  const submitRef = useRef<HTMLButtonElement>(null);
  const hasOptions = hasChipValues(props.options);

  return (
    <Controller
      control={props.control}
      name={props.name}
      defaultValue={props.defaultValue}
      rules={{
        validate: props.required
          ? (value) => value && value.text.length > 0
          : undefined,
      }}
      render={({ field: { onChange, value, onBlur } }) => {
        const handleOnChange = (newValue: RichTextValue) => {
          onChange(newValue);
        };

        // Because of a bug we had, there may be the case where this value is an array. If it is, we need to use the first element.
        // Added on March 25, 2025. Can be safely removed a while after that date.
        const unarrayedValue = Array.isArray(value) ? value[0] : value;

        // Large text inputs stored in cold storage: show read-only preview
        // with download link. The original TextContainer value is preserved
        // in the form so replays pass it through to the runner, which
        // resolves the full text from cloud storage.
        if (isTextPreviewContainer(unarrayedValue)) {
          return (
            <div className="w-full">
              {props.label && (
                <NoxusFormLabel
                  label={props.label}
                  tooltip={props.tooltip}
                  name={props.name}
                  isRequired={props.required}
                  isDisabled={props.isDisabled}
                />
              )}
              <TextPreviewInput value={unarrayedValue} />
            </div>
          );
        }

        // Normalize to RichTextValue. When definition is absent (backend sends only
        // { text, isRaw }), pass through as-is — TiptapEditor falls back to parsing
        // `text` as markdown via the tiptap-markdown extension.
        const parsedValue: RichTextValue =
          typeof unarrayedValue === "string"
            ? getTemplateStringValue(unarrayedValue, props.isSimpleText)
            : (unarrayedValue ?? { text: "", definition: {} });

        return (
          <div className="w-full">
            {props.label && (
              <NoxusFormLabel
                label={props.label}
                tooltip={props.tooltip}
                name={props.name}
                isRequired={props.required}
                isDisabled={props.isDisabled}
              />
            )}

            <ErrorBoundary
              fallback={
                <RichTextErrorFallback text={parsedValue?.text || ""} />
              }
            >
              <TiptapEditor
                onChange={handleOnChange}
                value={parsedValue}
                label={props.label}
                chipOptions={props.options}
                unselectAllNodes={props.unselectAllNodes}
                placeholder={props.placeholder}
                isControlled={props.isControlled}
                config={props.config}
                isSimpleText={props.isSimpleText}
                isDisabled={props.isDisabled}
                templateOptions={props.templateOptions}
                onKeyDown={props.onKeyDown}
              />
            </ErrorBoundary>
            {!props.helperText &&
              hasOptions &&
              !props.config?.hideChipOptions && (
                <span className="text-xs font-regular leading-normal text-muted-foreground mt-1.5 whitespace-pre-line">
                  Insert{" "}
                  <span className="font-semibold text-foreground bg-muted rounded-sm px-1.5">
                    /
                  </span>{" "}
                  for a variable value.
                </span>
              )}

            {!hasOptions && props.helperText && (
              <span className="text-xs font-regular leading-normal text-muted-foreground mt-1.5 whitespace-pre-line">
                {props.helperText}
              </span>
            )}

            <Button type="submit" style={{ display: "none" }} ref={submitRef} />
          </div>
        );
      }}
    />
  );
};

// Read-only fallback shown when TipTap fails to render the stored value.
// Past-run inputs occasionally contain shapes that crash
// prosemirror-markdown (e.g. trailing hardBreaks after bold marks); without
// this boundary the whole run-view tab unmounts.
const RichTextErrorFallback = ({ text }: { text: string }) => (
  <div className="rounded-md border border-input bg-muted/30 p-3">
    <NoxusAlert
      look="banner"
      variant="destructive"
      title="An error occurred while rendering this value."
    />
    {text && (
      <p className="mt-2 max-h-[200px] overflow-y-auto whitespace-pre-wrap break-words text-sm">
        {text}
      </p>
    )}
  </div>
);
